import { useCallback, useMemo } from "react";
import {
  toastManager,
  type EntitySelectorConfig,
  type EntitySelection,
} from "@salestrak/ui";
import type { EntityRefValue, ObjectAccessLevel } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useMentionAccessActions } from "#app/lib/mention-access-dialog-context";
import { canManageSharing } from "#app/lib/utils/access-level";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { issueHref } from "#app/lib/utils/entity-hrefs";
import { useIssueCardActions } from "./use-issue-card-actions";
import { useAnalytics, AnalyticsContext, KanbanEvent } from "#app/lib/analytics";
import { useOpenIssueTypeMigration } from "#app/lib/issue-type-migration-dialog-context";
import { useStagePool, STAGE_POOL_ID } from "#app/components/entity-selectors/hooks/use-stage-pool";
import { useHasMoveTargets } from "#app/components/issues/move-workflow-picker";
import { WORKFLOW_POOL_ID } from "#app/components/entity-selectors/hooks/use-workflow-pool";
import { useUserPool, USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import { useIssueTypePool, ISSUE_TYPE_POOL_ID } from "#app/components/entity-selectors/hooks/use-issue-type-pool";
import { assigneeRefToEntityItem } from "#app/components/entity-selectors/utils";
import type { Omittable } from "@salestrak/ui";

export { STAGE_POOL_ID, WORKFLOW_POOL_ID, USER_POOL_ID, ISSUE_TYPE_POOL_ID };

/** Minimal issue shape accepted by useIssueActions — both IssueSummaryFragment and Issue satisfy this. */
export type IssueActionRef = Omittable<{
  id: string;
  issueId: string;
  name: string;
  stageId: string;
  workflowId: string;
  issueTypeId?: string | null;
  archived?: boolean | null;
  assignees?: EntityRefValue[];
  // Resolved per-viewer access tier — drives gating on actions like
  // "Sharing & Permissions" (only viewers with `full_edit` see it).
  // Optional so callsites that don't track it stay valid.
  viewerAccessLevel?: ObjectAccessLevel | null;
  // Backend-resolved per-viewer mutation gates. `if_created` and other
  // ownership-conditional cell states collapse into these booleans so
  // the menu doesn't need to re-derive them from the flat perm map.
  viewerCanDelete?: boolean;
  viewerCanArchive?: boolean;
}>;

export function useIssueActions(issue: IssueActionRef) {
  const {
    renameIssue,
    archiveIssue,
    unarchiveIssue,
    deleteIssue,
    changeStage,
    toggleFavorite,
    isIssueFavorited,
    moveToWorkflow,
    updateAssignees,
  } = useIssueCardActions();
  const openTypeMigration = useOpenIssueTypeMigration();
  const { track } = useAnalytics();

  const { pool: stagePool } = useStagePool(issue.workflowId);
  // These pools feed the issue action MENUS (the Assignee submenu) — menu
  // rows keep their trailing gutter for the selection check, so the pool's
  // default Profile row action is dropped.
  const { pool: userPool } = useUserPool({ profileAction: false });
  const { issueTypes } = useWorkspaceData();
  const { pool: issueTypePool } = useIssueTypePool({ issueTypes });

  const isFavorited = isIssueFavorited(issue.issueId);

  const stageConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [stagePool],
      maxSelections: 1,
      searchPlaceholder: "Search stages...",
      emptyMessage: "No matching stages",
    }),
    [stagePool],
  );

  const stageValue = useMemo<EntitySelection[]>(() => {
    const item = stagePool.items.find((i) => i.id === issue.stageId);
    if (!item) return [];
    return [{ id: item.id, poolId: STAGE_POOL_ID, entity: item }];
  }, [stagePool.items, issue.stageId]);

  const issueTypeConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [issueTypePool],
      maxSelections: 1,
      searchPlaceholder: "Find type...",
      emptyMessage: "No Project types",
    }),
    [issueTypePool],
  );

  const issueTypeValue = useMemo<EntitySelection[]>(() => {
    const item = issueTypePool.items.find((i) => i.id === issue.issueTypeId);
    if (!item) return [];
    return [{ id: item.id, poolId: ISSUE_TYPE_POOL_ID, entity: item }];
  }, [issueTypePool.items, issue.issueTypeId]);

  const assigneeConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [userPool],
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
    }),
    [userPool],
  );

  const assigneeSelections = useMemo<EntitySelection[]>(() => {
    const poolMap = new Map(userPool.items.map((item) => [item.id, item]));
    return (issue.assignees ?? [])
      .filter((a) => a.user || poolMap.has(a.entityId))
      .map((a) => {
        // Assignees missing from the pool (e.g. removed members) still resolve
        // through the shared assignee-ref mapper, not an inline avatar config.
        const entity = poolMap.get(a.entityId) ?? assigneeRefToEntityItem(a);
        return { id: a.entityId, poolId: USER_POOL_ID, entity };
      });
  }, [issue.assignees, userPool.items]);

  const handleStageValueChange = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected && selected.id !== issue.stageId) {
        changeStage(issue, selected.id);
      }
    },
    [issue, changeStage],
  );

  // Mirrors the note composer's @-mention guard: after adding someone
  // as an assignee, the globally-mounted dialog checks whether
  // they can actually see the issue and prompts the actor to grant
  // access when they can't. Never blocks the update itself.
  const { promptIfMissing } = useMentionAccessActions();
  const viewerCanManageSharing = canManageSharing(issue.viewerAccessLevel);

  const handleAssigneeValueChange = useCallback(
    (selections: EntitySelection[]) => {
      // Diff against the current assignee set so only newly-added users
      // trigger the access prompt — removals and no-ops stay silent.
      const prevIds = new Set((issue.assignees ?? []).map((a) => a.entityId));
      const addedIds = selections
        .map((s) => s.id)
        .filter((id) => !prevIds.has(id));
      updateAssignees(issue.issueId, selections.map((s) => s.id));
      if (addedIds.length) {
        promptIfMissing({
          issueId: issue.issueId,
          userIds: addedIds,
          viewerCanManageSharing,
          variant: "assignment",
        });
      }
    },
    [
      issue.issueId,
      issue.assignees,
      updateAssignees,
      promptIfMissing,
      viewerCanManageSharing,
    ],
  );

  // The menu's "Move to Workflow…" submenu hosts the shared rows (a
  // submenu per workflow, its stages inside) — see MoveWorkflowRows.
  const handleMoveWorkflow = useCallback(
    (workflowId: string, stageId: string) => {
      void moveToWorkflow(issue, workflowId, stageId);
    },
    [issue, moveToWorkflow],
  );
  const hasOtherWorkflows = useHasMoveTargets(issue.workflowId);

  const handleIssueTypeValueChange = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected && selected.id !== issue.issueTypeId) {
        // The migrate hook sends `kanban.change_type` once the server accepts.
        openTypeMigration({ issueId: issue.issueId, issueTypeId: selected.id, via: "menu" });
      }
    },
    [issue.issueId, issue.issueTypeId, openTypeMigration],
  );

  const handleFavorite = useCallback(() => {
    toggleFavorite(issue.issueId);
  }, [issue.issueId, toggleFavorite]);

  const { activeWorkspace } = useWorkspace();
  const handleCopyLink = useCallback(async () => {
    const url = issueHref(
      activeWorkspace?.urlPath ?? "",
      issue.workflowId,
      issue.issueId,
    );
    const ok = await copyToClipboard(url);
    if (ok) {
      track({ context: AnalyticsContext.Kanban, event: KanbanEvent.CopyLink });
      toastManager.add({ title: "Link copied", type: "success" });
    } else {
      toastManager.add({ title: "Failed to copy link", type: "error" });
    }
  }, [activeWorkspace?.urlPath, issue.workflowId, issue.issueId, track]);

  const handleOpenInNewTab = useCallback(() => {
    const url = issueHref(
      activeWorkspace?.urlPath ?? "",
      issue.workflowId,
      issue.issueId,
    );
    window.open(url, "_blank", "noopener");
    track({ context: AnalyticsContext.Kanban, event: KanbanEvent.OpenInNewTab });
  }, [activeWorkspace?.urlPath, issue.workflowId, issue.issueId, track]);

  const handleArchive = useCallback(async () => {
    if (issue.archived) {
      await unarchiveIssue(issue);
    } else {
      await archiveIssue(issue);
    }
  }, [issue, archiveIssue, unarchiveIssue]);

  const handleDelete = useCallback(async () => {
    await deleteIssue(issue);
  }, [issue, deleteIssue]);

  return {
    isFavorited,
    isArchived: !!issue.archived,
    hasStages: stagePool.items.length > 0,
    hasOtherWorkflows,
    workflowId: issue.workflowId,
    hasIssueTypes: issueTypePool.items.length > 0,
    stageConfig,
    stageValue,
    issueTypeConfig,
    issueTypeValue,
    assigneeConfig,
    assigneeSelections,
    handleStageValueChange,
    handleAssigneeValueChange,
    handleMoveWorkflow,
    handleIssueTypeValueChange,
    handleFavorite,
    handleCopyLink,
    handleOpenInNewTab,
    handleArchive,
    handleDelete,
    renameIssue,
  };
}
